/**
 * Registry of long-form educational guides ("What is X" / "X explained").
 *
 * This module is intentionally free of React/JSX imports so the build scripts
 * (generate-seo.ts, prerender.ts) can import it to enumerate guide routes for
 * the sitemap, llms.txt and prerendering without pulling in the UI.
 *
 * The article BODY for each guide is a React component, wired by slug in
 * GuidePage.tsx. Everything an LLM or crawler reads about the page (title,
 * description, FAQ, related links) lives here so it stays in one place and
 * feeds both the visible page and its JSON-LD.
 *
 * Guides are locale-aware: English guides serve from logpulse.io; localized
 * guides (locale: 'nl' | 'de' | 'fr') serve from their ccTLD at the same
 * /guides/<slug> path structure. A localized guide links to its English
 * counterpart via `altOf`, which drives bidirectional hreflang on both pages.
 * When adding the FIRST guide in a new locale, also add that locale to
 * GUIDE_LOCALES in src/lib/site-config.ts (kept as a literal there so the
 * main bundle does not need to import this registry).
 */

export type GuideLocale = 'en' | 'nl' | 'de' | 'fr' | 'es' | 'it';

export interface GuideFaq {
  q: string;
  a: string;
}

export interface GuideRelatedLink {
  label: string;
  href: string;
}

export type GuideCategory = 'Compliance' | 'Security' | 'AI & Agents' | 'Log management';

export interface GuideMeta {
  /** URL slug under /guides/<slug>. */
  slug: string;
  /** Visible H1 + hub card title. */
  title: string;
  /** <title> tag: keep ~60 chars, lead with the head keyword. */
  metaTitle: string;
  /** Meta description: ~150-160 chars. */
  description: string;
  /** One-line summary for the hub card + Article JSON-LD description. */
  excerpt: string;
  category: GuideCategory;
  /** Human-readable reading time, e.g. '8 min'. */
  readTime: string;
  /** ISO date (YYYY-MM-DD). */
  datePublished: string;
  dateModified: string;
  /** Rendered visibly AND as FAQPage JSON-LD (Google policy requires both). */
  faq: GuideFaq[];
  /** Contextual cross-links rendered near the foot of the article. */
  related: GuideRelatedLink[];
  /** Language of the guide; determines which domain serves it. Default 'en'. */
  locale?: GuideLocale;
  /** Localized guides only: slug of the English counterpart (for hreflang). */
  altOf?: string;
}

/** Display order for category groupings on the hub. */
export const GUIDE_CATEGORY_ORDER: GuideCategory[] = [
  'Compliance',
  'Security',
  'AI & Agents',
  'Log management',
];

export const guides: GuideMeta[] = [
  {
    slug: 'uitgebuite-cve-welke-raken-jou',
    locale: 'nl',
    title: "Uitgebuite CVE's: welke raken jouw systemen?",
    metaTitle: "Uitgebuite CVE's: welke raken jou? (KEV, EPSS) | LogPulse",
    description:
      "Van een CVE in het nieuws naar een antwoord in je eigen data: KEV en EPSS, je kwetsbaarheidsscans, een geteste detectie en 30 dagen terugkijken in je logs.",
    excerpt:
      "In vier stappen van een CVE in het nieuws (GitLab, Cisco, WordPress) naar het antwoord: staat het lek bij ons en zouden we misbruik zien?",
    category: 'Security',
    readTime: '6 min',
    datePublished: '2026-11-04',
    dateModified: '2026-11-04',
    faq: [
      {
        q: 'Wat is de CISA KEV-lijst?',
        a: "De Known Exploited Vulnerabilities-lijst van het Amerikaanse CISA bevat kwetsbaarheden waarvan vaststaat dat ze actief worden misbruikt. Het is de snelste manier om uit tienduizenden CVE's de lekken te halen die nu echt gebruikt worden.",
      },
      {
        q: 'Wat is het verschil tussen CVSS en EPSS?',
        a: 'CVSS zegt hoe ernstig een lek is als het misbruikt wordt. EPSS schat hoe waarschijnlijk het is dat het lek in de komende 30 dagen misbruikt wordt. Samen met de KEV-lijst bepalen ze wat je eerst patcht.',
      },
      {
        q: 'Hoe weet ik of een CVE op mijn systemen staat?',
        a: 'Uit je kwetsbaarheidsscans. Stuur de uitvoer van Trivy, Grype, Snyk of Clair naar LogPulse, dan worden de bevindingen elke zes uur naast KEV, EPSS en NVD gelegd. Zonder scans kan geen enkel platform dat betrouwbaar zeggen.',
      },
      {
        q: 'Vervangt LogPulse patchen?',
        a: 'Nee. Patchen is de oplossing. LogPulse laat zien welke uitgebuite lekken bij je draaien, of een geteste regel op misbruik let en of er in de afgelopen 30 dagen al pogingen in je logs stonden.',
      },
    ],
    related: [
      { label: 'Threat intelligence: van CVE tot detectie', href: '/threat-intelligence' },
      { label: 'Securitymonitoring (SIEM)', href: '/siem' },
      { label: 'Cyberbeveiligingswet (NIS2)', href: '/guides/nis2-cyberbeveiligingswet' },
      { label: 'Lekken die met een inlog begonnen', href: '/guides/datalek-via-inlog-herkennen' },
    ],
  },
  {
    slug: 'ai-agents-webwinkels-aanvallen',
    locale: 'nl',
    title: 'AI-agents vallen webwinkels aan: wat zie je in je logs?',
    metaTitle: 'AI-agents vallen webwinkels aan: signalen in je logs | LogPulse',
    description:
      'Autonome AI-agents vielen ruim 1.200 webwinkels aan voor zo’n $50 per hack. Welke sporen ze achterlaten, in welke logs en wat je daarmee kunt zien.',
    excerpt:
      'Wat een aanval door een AI-agent op een webwinkel achterlaat: het afspeuren van beheerpaden, bekende lekken, webshells en aanvals-IP’s.',
    category: 'Security',
    readTime: '5 min',
    datePublished: '2026-11-04',
    dateModified: '2026-11-04',
    faq: [
      {
        q: 'Hoe vallen AI-agents een webwinkel aan?',
        a: 'Ze brengen de site in kaart, proberen bekende lekken in plugins en het CMS en plaatsen daarna een webshell: een script dat de aanvaller blijvende toegang geeft. Elke stap loopt via HTTP en komt dus in je webserverlogs.',
      },
      {
        q: 'Welke logs heb ik nodig om dit te zien?',
        a: 'Access logs van Apache of nginx in het combined-formaat voor het afspeuren en de IP-adressen. Voor webshell-payloads heb je een bron met request bodies nodig, zoals je WAF of een applicatielog.',
      },
      {
        q: 'Houdt LogPulse zo’n aanval tegen?',
        a: 'Nee, LogPulse detecteert en blokkeert niet zelf. Een WAF, een bijgewerkt CMS en weinig plugins doen het meeste werk. LogPulse laat zien of iemand het geprobeerd heeft en of het gelukt is.',
      },
    ],
    related: [
      { label: 'Threat intelligence: van CVE tot detectie', href: '/threat-intelligence' },
      { label: "Uitgebuite CVE's: welke raken jou?", href: '/guides/uitgebuite-cve-welke-raken-jou' },
      { label: 'Securitymonitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'datalek-via-inlog-herkennen',
    locale: 'nl',
    title: 'Lekken die met een inlog begonnen: zou jij het zien?',
    metaTitle: 'Datalek via een inlog herkennen: 5 signalen | LogPulse',
    description:
      'Password spraying, brute force, MFA-moeheid, MFA uitgezet en legacy auth: vijf inlogsignalen die je in Entra ID, Okta en je servers kunt bewaken.',
    excerpt:
      'Een aantal grote incidenten van oktober 2026 begon met een geldige inlog. Vijf inlogsignalen die het bewaken waard zijn en waar ze vandaan komen.',
    category: 'Security',
    readTime: '5 min',
    datePublished: '2026-11-04',
    dateModified: '2026-11-04',
    faq: [
      {
        q: 'Wat is password spraying?',
        a: 'Een aanval waarbij één bron een paar veelgebruikte wachtwoorden probeert op veel accounts tegelijk, net onder de lockout-grens. Je herkent het aan één IP dat op veel verschillende accounts faalt.',
      },
      {
        q: 'Wat is MFA-moeheid?',
        a: 'Een aanvaller met een gestolen wachtwoord stuurt de gebruiker een reeks MFA-pushmeldingen, tot die er een goedkeurt. Herhaald mislukte MFA-pogingen op één account zijn het signaal.',
      },
      {
        q: 'Welke logs heb ik nodig?',
        a: 'De inlog- en auditlogs van Microsoft Entra ID of Okta, die je zonder installatie koppelt en voor servers sshd, sudo en het Windows Security-log via de LogPulse-agent.',
      },
      {
        q: 'Vervangt dit MFA?',
        a: 'Nee. MFA, een wachtwoordmanager en EDR blijven nodig. Inlogdetectie laat zien wanneer die maatregelen op de proef worden gesteld en of er een faalde.',
      },
    ],
    related: [
      { label: 'Threat intelligence: van CVE tot detectie', href: '/threat-intelligence' },
      { label: 'Meldplicht Cyberbeveiligingswet', href: '/guides/meldplicht-cyberbeveiligingswet' },
      { label: 'Securitymonitoring (SIEM)', href: '/siem' },
      { label: 'AI-agents vallen webwinkels aan', href: '/guides/ai-agents-webwinkels-aanvallen' },
    ],
  },
  {
    slug: 'nis2-logging-requirements',
    title: 'NIS2 logging requirements explained',
    metaTitle: 'NIS2 Logging Requirements Explained (2026) | LogPulse',
    description:
      'What NIS2 requires for logging and monitoring: what to log, how long to keep logs, retention, continuous monitoring, and the 24h/72h/1-month incident reporting deadlines.',
    excerpt:
      'What to log, how long to keep it, the monitoring duty, and the 24h/72h/1-month incident reporting deadlines under the NIS2 Directive.',
    category: 'Compliance',
    readTime: '9 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'How long do you need to keep logs under NIS2?',
        a: 'NIS2 sets no single statutory retention period. It requires retention appropriate to the risk: long enough to detect, investigate and report an incident. In practice organisations keep logs for roughly 6 to 18 months, often split into a hot tier and an archive, and some national transpositions set their own minimums. Always check your national implementing law.',
      },
      {
        q: 'Does NIS2 require a SIEM?',
        a: 'NIS2 does not name any product category, but it requires continuous monitoring, correlation of events across sources, threshold-based alerting and a timely qualified response. That is the functional definition of a SIEM, so most in-scope organisations need SIEM-like capability to comply.',
      },
      {
        q: 'What logs does NIS2 require you to collect?',
        a: 'A defensible baseline covers authentication and access events, administrative and privileged actions, configuration and change events, network and security telemetry, application and system events, and access to sensitive data, each with accurate timestamps, identity, source and outcome.',
      },
      {
        q: 'What are the NIS2 incident reporting deadlines?',
        a: 'Under Article 23: an early warning within 24 hours, an incident notification within 72 hours, and a final report within one month. Your logs are the evidence base for each stage, so fast search and complete retention are essential.',
      },
      {
        q: 'When did NIS2 take effect?',
        a: 'NIS2 (Directive (EU) 2022/2555) had a transposition deadline of 17 October 2024. It applies through each Member State’s national law, and several countries transposed it late, so the binding details depend on your national legislation.',
      },
    ],
    related: [
      { label: 'NIS2 compliance with LogPulse', href: '/compliance/nis2' },
      { label: 'DORA logging requirements', href: '/guides/dora-logging-monitoring-requirements' },
      { label: 'Log retention requirements', href: '/guides/log-retention-requirements' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'NIS2 logging checklist (blog)', href: '/blog/nis2-logging-requirements-checklist' },
    ],
  },
  {
    slug: 'nis2-cyberbeveiligingswet',
    locale: 'nl',
    altOf: 'nis2-logging-requirements',
    title: 'NIS2 in Nederland: de Cyberbeveiligingswet uitgelegd',
    metaTitle: 'NIS2 in Nederland: Cyberbeveiligingswet, zorgplicht & logging | LogPulse',
    description:
      'Wat NIS2 en de Cyberbeveiligingswet betekenen voor Nederlandse organisaties: wie eronder valt, de zorgplicht en meldplicht, toezicht door de RDI, boetes, en wat het concreet vraagt van je logging en monitoring.',
    excerpt:
      'Wie onder NIS2 valt, wat de zorgplicht en meldplicht inhouden, hoe het toezicht door de RDI werkt, en wat de Cyberbeveiligingswet concreet vraagt van je logging en monitoring.',
    category: 'Compliance',
    readTime: '10 min',
    datePublished: '2026-07-04',
    dateModified: '2026-08-11',
    faq: [
      {
        q: 'Wat is de Cyberbeveiligingswet?',
        a: 'De Cyberbeveiligingswet (Cbw) is de Nederlandse implementatie van de Europese NIS2-richtlijn en de opvolger van de Wet beveiliging netwerk- en informatiesystemen (Wbni). De wet is per 15 augustus 2026 van kracht, zonder overgangsperiode. Zij legt essentiële en belangrijke entiteiten een zorgplicht (beveiligingsmaatregelen, waaronder logging en monitoring), een meldplicht voor significante incidenten en een registratieplicht bij het NCSC op, met toezicht door de RDI en sectorale toezichthouders.',
      },
      {
        q: 'Valt mijn organisatie onder NIS2?',
        a: 'NIS2 geldt voor middelgrote en grote organisaties (vanaf circa 50 medewerkers of 10 miljoen euro jaaromzet) in aangewezen sectoren, van energie, transport, zorg en digitale infrastructuur tot post, afvalbeheer, chemie en digitale aanbieders. Sommige organisaties vallen er ongeacht omvang onder, bijvoorbeeld als enige aanbieder van een essentiële dienst. Doe de NIS2-zelfevaluatie van de Rijksoverheid om het formeel vast te stellen.',
      },
      {
        q: 'Wat is het verschil tussen de Wbni en de Cyberbeveiligingswet?',
        a: 'De Wbni implementeerde de eerste NIS-richtlijn en raakte enkele honderden aanbieders van essentiële diensten. De Cyberbeveiligingswet implementeert NIS2 en is veel breder: meer sectoren, ook middelgrote bedrijven, expliciete eisen aan risicobeheer (waaronder logging, detectie en incidentafhandeling), persoonlijke verantwoordelijkheid van bestuurders en fors hogere boetes.',
      },
      {
        q: 'Hoe lang moet ik logs bewaren onder NIS2?',
        a: 'NIS2 en de Cyberbeveiligingswet noemen geen vaste bewaartermijn. De norm is risicogebaseerd: lang genoeg om een incident te kunnen detecteren, onderzoeken en melden, inclusief de eindrapportage die binnen een maand af moet zijn. In de praktijk hanteren organisaties 6 tot 18 maanden, vaak gesplitst in een doorzoekbare hot-laag en een goedkoper archief.',
      },
      {
        q: 'Maakt LogPulse mijn organisatie NIS2-compliant?',
        a: 'Nee, geen enkel product maakt je vanzelf compliant. LogPulse dekt wel de logging-, detectie- en monitoringkant van de zorgplicht: gecentraliseerd logbeheer met instelbare retentie, realtime detecties met MITRE ATT&CK-mapping, incident-workflows die de 24-uurs- en 72-uursmeldingen onderbouwen, en compliance-rapportages die detecties aan NIS2-controls koppelen. De data blijft in de EU (GCP Amsterdam).',
      },
    ],
    related: [
      { label: 'De meldplicht: het 24-uursdraaiboek', href: '/guides/meldplicht-cyberbeveiligingswet' },
      { label: 'NIS2-compliance met LogPulse', href: '/compliance/nis2' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'Deze gids in het Engels', href: '/guides/nis2-netherlands-cyberbeveiligingswet' },
      { label: 'NIS2 logging requirements (Engels, verdieping)', href: '/guides/nis2-logging-requirements' },
      { label: 'Prijzen', href: '/pricing' },
    ],
  },
  {
    // NL-only verdiepingsgids op de meldplicht; bewust geen altOf (er is geen
    // Engelse evenknie), de hreflang-guards in GuidePage/generate-seo slaan
    // alternates dan over.
    slug: 'meldplicht-cyberbeveiligingswet',
    locale: 'nl',
    title: 'De meldplicht onder de Cyberbeveiligingswet: het 24-uursdraaiboek',
    metaTitle: 'Meldplicht Cyberbeveiligingswet: het draaiboek | LogPulse',
    description:
      'Sinds 15 augustus 2026 verplicht de Cyberbeveiligingswet melding van significante incidenten: binnen 24 uur een vroegtijdige waarschuwing, binnen 72 uur een volledige melding en binnen een maand een eindrapport. Het draaiboek: wat je meldt, bij wie, en wat je nu klaarzet.',
    excerpt:
      'Binnen 24 uur een vroegtijdige waarschuwing, binnen 72 uur de volledige melding, binnen een maand het eindrapport. Wat er in elke melding moet, bij wie je meldt, en de zes dingen die je vandaag klaarzet.',
    category: 'Compliance',
    readTime: '8 min',
    datePublished: '2026-08-12',
    dateModified: '2026-08-12',
    faq: [
      {
        q: 'Wanneer moet ik een incident melden onder de Cyberbeveiligingswet?',
        a: 'Bij een significant incident: een incident dat een ernstige operationele verstoring of financiële verliezen veroorzaakt of kan veroorzaken, of dat anderen aanzienlijke materiële of immateriële schade toebrengt of kan toebrengen. De meldketen kent drie deadlines: een vroegtijdige waarschuwing binnen 24 uur, een volledige melding binnen 72 uur en een eindrapport binnen één maand.',
      },
      {
        q: 'Bij wie meld ik een incident?',
        a: 'Bij het CSIRT (voor de meeste organisaties het NCSC) en bij de toezichthouder, voor veel sectoren de Rijksinspectie Digitale Infrastructuur (RDI). In de praktijk verloopt de melding via het meldportaal van het NCSC als één loket. Zorg dat je registratie en toegang tot dat portaal geregeld zijn vóórdat je ze nodig hebt.',
      },
      {
        q: 'Wat als ik binnen 24 uur nog bijna niets weet?',
        a: 'Dat is ingecalculeerd: de vroegtijdige waarschuwing mag beperkt zijn. Je meldt dat er een significant incident is, of je kwaadwillig handelen vermoedt en of er grensoverschrijdende gevolgen kunnen zijn. Wachten tot het volledige beeld er is, is de fout: de 72-uursmelding en het eindrapport zijn er voor de verdieping.',
      },
      {
        q: 'Geldt de meldplicht ook voor belangrijke entiteiten?',
        a: 'Ja. De meldplicht en de deadlines zijn identiek voor essentiële en belangrijke entiteiten. Het verschil zit in het toezicht (proactief versus reactief) en in de maximale boetes: tot 10 miljoen euro of 2% van de wereldwijde jaaromzet voor essentiële entiteiten, tot 7 miljoen euro of 1,4% voor belangrijke entiteiten.',
      },
      {
        q: 'Hoe helpt logging bij de meldplicht?',
        a: 'Elke fase van de meldketen leunt op logs: de 24-uursmelding op detectie die het incident opmerkt, de 72-uursmelding op doorzoekbare logs met indicatoren van compromittatie, het eindrapport op een volledige reconstructie met oorzaak. Gecentraliseerde logging met detectie, zoals LogPulse biedt, is de technische fundering; compliant word je pas met het bijbehorende proces en de mensen die het uitvoeren.',
      },
    ],
    related: [
      { label: 'Val ik onder de Cbw? Doe de check op cyberplicht.nl', href: 'https://cyberplicht.nl/' },
      { label: 'NIS2 in Nederland: de Cyberbeveiligingswet uitgelegd', href: '/guides/nis2-cyberbeveiligingswet' },
      { label: 'NIS2-compliance met LogPulse', href: '/compliance/nis2' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'Prijzen', href: '/pricing' },
    ],
  },
  {
    slug: 'nis2-umsetzungsgesetz',
    locale: 'de',
    altOf: 'nis2-logging-requirements',
    title: 'NIS2 in Deutschland: das Umsetzungsgesetz erklärt',
    metaTitle: 'NIS2 in Deutschland: Umsetzungsgesetz, BSI & Logging | LogPulse',
    description:
      'Was NIS2 und das deutsche Umsetzungsgesetz (NIS2UmsuCG) für Ihre Organisation bedeuten: wer betroffen ist, Risikomanagement- und Meldepflichten, BSI-Aufsicht, Bußgelder, und was das konkret für Logging und Monitoring verlangt.',
    excerpt:
      'Wer unter NIS2 fällt, was das NIS2-Umsetzungsgesetz an Risikomanagement- und Meldepflichten bringt, wie die BSI-Aufsicht funktioniert, und was das konkret für Logging und Monitoring bedeutet.',
    category: 'Compliance',
    readTime: '10 min',
    datePublished: '2026-07-04',
    dateModified: '2026-07-04',
    faq: [
      {
        q: 'Was ist das NIS2-Umsetzungsgesetz (NIS2UmsuCG)?',
        a: 'Das NIS2-Umsetzungs- und Cybersicherheitsstärkungsgesetz (NIS2UmsuCG) setzt die europäische NIS2-Richtlinie in deutsches Recht um, im Kern durch eine umfassende Novelle des BSI-Gesetzes (BSIG). Es ist seit dem 6. Dezember 2025 in Kraft, ohne Übergangsfrist für die Maßnahmen, und führt die Kategorien "besonders wichtige Einrichtungen" und "wichtige Einrichtungen" ein, mit Risikomanagement-Pflichten, einer dreistufigen Meldepflicht, einer Registrierungspflicht beim BSI und persönlichen Pflichten der Geschäftsleitung.',
      },
      {
        q: 'Ist mein Unternehmen von NIS2 betroffen?',
        a: 'NIS2 gilt für mittlere und große Unternehmen (ab etwa 50 Beschäftigten oder 10 Millionen Euro Jahresumsatz) in den regulierten Sektoren, von Energie, Transport, Gesundheit und digitaler Infrastruktur bis Post, Abfall, Chemie, verarbeitendem Gewerbe und digitalen Diensten. Insgesamt betrifft das in Deutschland Schätzungen zufolge rund 30.000 Unternehmen, ein Vielfaches der bisherigen KRITIS-Regulierung. Das BSI bietet eine offizielle Online-Betroffenheitsprüfung an.',
      },
      {
        q: 'Was ist der Unterschied zwischen KRITIS und NIS2?',
        a: 'Die KRITIS-Regulierung erfasst Betreiber kritischer Anlagen ab bestimmten Schwellenwerten und verlangt von ihnen bereits heute unter anderem Systeme zur Angriffserkennung. NIS2 ist deutlich breiter: Sie erfasst ganze Sektoren nach Unternehmensgröße statt nur einzelne Anlagen. KRITIS-Betreiber bleiben eine eigene, strenger regulierte Kategorie innerhalb des neuen Rahmens.',
      },
      {
        q: 'Wie lange müssen Logs unter NIS2 aufbewahrt werden?',
        a: 'Weder die Richtlinie noch das deutsche Umsetzungsgesetz nennen eine feste Aufbewahrungsfrist. Der Maßstab ist risikobasiert: lang genug, um Vorfälle zu erkennen, zu untersuchen und zu melden, einschließlich des Abschlussberichts nach einem Monat. In der Praxis üblich sind 6 bis 18 Monate, oft aufgeteilt in eine durchsuchbare Hot-Ebene und ein günstigeres Archiv.',
      },
      {
        q: 'Macht LogPulse mein Unternehmen NIS2-konform?',
        a: 'Nein, kein Produkt macht Sie von allein konform. LogPulse deckt aber die Logging-, Detektions- und Monitoring-Seite der Pflichten ab: zentralisiertes Log-Management mit konfigurierbarer Aufbewahrung, Echtzeit-Detektionen mit MITRE-ATT&CK-Zuordnung, Incident-Workflows, die die 24- und 72-Stunden-Meldungen mit Belegen unterfüttern, und Compliance-Berichte, die Detektionen NIS2-Anforderungen zuordnen. Alle Daten bleiben in der EU (GCP Amsterdam).',
      },
    ],
    related: [
      { label: 'NIS2-Compliance mit LogPulse', href: '/compliance/nis2' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'NIS2 logging requirements (Englisch, Vertiefung)', href: '/guides/nis2-logging-requirements' },
      { label: 'Preise', href: '/pricing' },
    ],
  },
  {
    // Engelstalige gids over de Nederlandse wet, voor lezers die in Nederland
    // opereren maar geen Nederlands lezen. Bewust géén altOf: de en-alternate
    // van de NL-gids is de generieke NIS2-gids; deze staat op zichzelf.
    slug: 'nis2-netherlands-cyberbeveiligingswet',
    title: 'NIS2 in the Netherlands: the Cyberbeveiligingswet explained',
    metaTitle: 'NIS2 in the Netherlands: the Dutch Cbw | LogPulse',
    description:
      'An English guide to the Dutch Cyberbeveiligingswet (Cbw), in force 15 August 2026: who is covered, the duty of care, the 24-hour reporting chain, RDI supervision, and what it means for logging and monitoring.',
    excerpt:
      'The Netherlands implements NIS2 through the Cyberbeveiligingswet, in force from 15 August 2026 with no transition period. If you operate in the Netherlands but do not read Dutch, this guide explains who is covered, what the law requires, and what it means for your logging and monitoring.',
    category: 'Compliance',
    readTime: '10 min',
    datePublished: '2026-08-12',
    dateModified: '2026-08-12',
    faq: [
      {
        q: 'What is the Cyberbeveiligingswet (Cbw)?',
        a: 'The Cyberbeveiligingswet is the Dutch Cybersecurity Act that implements the EU NIS2 directive in the Netherlands, replacing the earlier Wbni. It enters into force on 15 August 2026 with no transition period: the duty of care, the incident notification duty and the registration duty with the NCSC apply immediately from that date.',
      },
      {
        q: 'Does the Cbw apply to international companies with a Dutch entity?',
        a: 'Yes, if the Dutch entity operates in one of the eighteen covered sectors and meets the size threshold of roughly 50 employees or 10 million euro in annual turnover. Smaller organisations can also be covered, for instance as part of a larger group or as the sole provider of an essential service. The Rijksoverheid offers an online NIS2 self-assessment, available in English, to check formally.',
      },
      {
        q: 'What are the incident reporting deadlines under the Cbw?',
        a: 'Significant incidents must be reported to the NCSC and the supervisory authority in three steps: an early warning within 24 hours, a full incident notification with an initial impact assessment and available IOCs within 72 hours, and a final report within one month. Meeting the 24-hour deadline requires that your logs already exist, are searchable, and that detection noticed the incident.',
      },
      {
        q: 'Are directors personally responsible under the Cbw?',
        a: 'Yes. The management board must approve the security measures, oversee their implementation and complete cybersecurity training. In cases of serious negligence, directors can be held personally liable, and for essential entities the regulator can, as a last resort, have directors temporarily suspended from their duties.',
      },
      {
        q: 'How long must logs be retained under NIS2 in the Netherlands?',
        a: 'The law sets no fixed retention period; the criterion is that you can investigate and report incidents. In practice, a risk-based 6 to 18 months is common, with a searchable hot tier and a cheaper archive. Document your choice and reasoning: that is exactly what the supervisory authority will ask for. No product makes you compliant by itself, but centralised logging with demonstrable retention is a foundation you can put in place today.',
      },
    ],
    related: [
      { label: 'NIS2 compliance with LogPulse', href: '/compliance/nis2' },
      { label: 'NIS2 logging requirements', href: '/guides/nis2-logging-requirements' },
      { label: 'LogPulse SIEM', href: '/siem' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    slug: 'nis2-france-loi-resilience',
    locale: 'fr',
    altOf: 'nis2-logging-requirements',
    title: 'NIS2 en France : où en est la loi et comment se préparer',
    metaTitle: 'NIS2 en France : transposition et obligations | LogPulse',
    description:
      "Où en est la transposition de NIS2 en France : le projet de loi résilience et cybersécurité, la saisine de la CJUE, le rôle de l'ANSSI, qui est concerné, les obligations et délais de notification, et ce que cela signifie concrètement pour le logging et le monitoring.",
    excerpt:
      "La loi française de transposition de NIS2 n'est toujours pas adoptée, mais les exigences de la directive sont déjà connues : qui est concerné, ce que l'ANSSI attend, les délais de notification, et ce que cela signifie pour le logging et le monitoring.",
    category: 'Compliance',
    readTime: '10 min',
    datePublished: '2026-08-12',
    dateModified: '2026-08-12',
    faq: [
      {
        q: 'La loi de transposition NIS2 est-elle adoptée en France ?',
        a: "Non. Le projet de loi relatif à la résilience des infrastructures critiques et au renforcement de la cybersécurité, qui regroupe trois volets (REC, NIS2 et DORA), a été adopté par le Sénat en première lecture le 12 mars 2025, puis dans une version amendée par la commission spéciale de l'Assemblée nationale le 10 septembre 2025. À ce jour, la loi n'est pas définitivement adoptée ni promulguée, et le 8 juillet 2026 la Commission européenne a saisi la Cour de justice de l'UE contre la France pour non-transposition, en demandant des sanctions financières.",
      },
      {
        q: 'Qui sera concerné par NIS2 en France ?',
        a: "Environ 15 000 entités françaises devraient entrer dans le champ : les organisations moyennes et grandes (à partir d'environ 50 salariés ou 10 millions d'euros de chiffre d'affaires) dans les secteurs régulés, de l'énergie, des transports, de la santé et de l'infrastructure numérique jusqu'aux services postaux, aux déchets, à la chimie, à l'industrie manufacturière et aux fournisseurs numériques. L'ANSSI sera l'autorité compétente ; le secteur financier relève de l'ACPR et de l'AMF via DORA.",
      },
      {
        q: "Quels sont les délais de notification d'un incident sous NIS2 ?",
        a: "La directive fixe une chaîne en trois étapes : une alerte précoce sous 24 heures avec une première appréciation d'un éventuel acte malveillant, une notification complète sous 72 heures avec l'évaluation de la gravité et les indicateurs de compromission disponibles, et un rapport final sous un mois. Tenir ces délais suppose que les logs pertinents existent déjà, soient interrogeables et que la détection ait remarqué l'incident.",
      },
      {
        q: 'Combien de temps faut-il conserver les logs pour NIS2 ?',
        a: "La directive ne fixe aucune durée de rétention : le critère est risk-based, c'est-à-dire assez longtemps pour détecter un incident, enquêter et le notifier, rapport final compris. En pratique, 6 à 18 mois sont courants, souvent répartis entre une couche interrogeable à chaud et une archive moins coûteuse. Ce choix et sa justification doivent être documentés.",
      },
      {
        q: 'Faut-il attendre la loi française pour se préparer ?',
        a: "Non, c'est le piège. Les exigences sont déjà fixées par la directive (UE) 2022/2555, et l'ANSSI a annoncé qu'après la publication des référentiels techniques les entités disposeront d'environ trois ans pour se mettre pleinement en conformité, avec l'analyse des risques et les plans de sécurisation attendus dès fin 2026, puis le déploiement de mesures comme le MFA et les sauvegardes en 2027. Cartographier son SI et bâtir une vraie capacité de logging et de détection prend du temps : commencer maintenant, c'est transformer le retard législatif en avance opérationnelle.",
      },
    ],
    related: [
      { label: 'La conformité NIS2 avec LogPulse', href: '/compliance/nis2' },
      { label: 'Security monitoring (SIEM)', href: '/siem' },
      { label: 'NIS2 logging requirements (anglais, pour approfondir)', href: '/guides/nis2-logging-requirements' },
      { label: 'Tarifs', href: '/pricing' },
    ],
  },
  {
    slug: 'nis2-espana-ley-ciberseguridad',
    locale: 'es',
    altOf: 'nis2-logging-requirements',
    title: 'NIS2 en España: la transposición, las obligaciones y qué hacer ya',
    metaTitle: 'NIS2 en España: estado de la ley y obligaciones | LogPulse',
    description:
      'España aún no ha transpuesto NIS2: el anteproyecto sigue en tramitación y Bruselas exige urgencia. Qué exigirá la ley, a quién afecta y por qué el logging y la detección no pueden esperar al BOE.',
    excerpt:
      'La ley española de transposición de NIS2 sigue en tramitación, pero los requisitos ya están fijados en la directiva. A quién afectará, qué exigirá y cómo preparar hoy tu logging y monitorización.',
    category: 'Compliance',
    readTime: '10 min',
    datePublished: '2026-08-12',
    dateModified: '2026-08-12',
    faq: [
      {
        q: '¿Ha transpuesto España ya la directiva NIS2?',
        a: 'No. España incumplió el plazo del 17 de octubre de 2024. La transposición avanza mediante el Anteproyecto de Ley de Coordinación y Gobernanza de la Ciberseguridad, aprobado por el Consejo de Ministros el 14 de enero de 2025, pero a mediados de 2026 sigue en tramitación y no se ha publicado en el BOE.',
      },
      {
        q: 'Si la ley española no está en vigor, ¿puedo esperar a que se publique?',
        a: 'Esperar es una mala estrategia. Los requisitos esenciales ya están fijados en la Directiva (UE) 2022/2555: gestión de riesgos con logging y detección, notificación escalonada de incidentes y responsabilidad de la dirección. La Comisión Europea exigió en mayo de 2026 una transposición urgente, así que la ley puede llegar en cualquier momento y sin periodos de gracia generosos.',
      },
      {
        q: '¿Qué plazos de notificación de incidentes impone NIS2?',
        a: 'Tres pasos: una alerta temprana en 24 horas, una notificación completa en 72 horas con evaluación de gravedad e indicadores de compromiso, y un informe final en un mes. Cumplirlos exige que los logs relevantes ya existan y sean consultables antes del incidente.',
      },
      {
        q: '¿Qué sanciones prevé el anteproyecto español?',
        a: 'Multas de hasta 10 millones de euros para los incumplimientos más graves, además de facultades de supervisión proactiva sobre las entidades esenciales y reactiva sobre las importantes, y responsabilidad al nivel de la dirección por aprobar y supervisar las medidas.',
      },
      {
        q: '¿Cuánto tiempo debo conservar los logs para NIS2?',
        a: 'La directiva no fija un plazo concreto: el criterio es poder investigar y notificar incidentes. En la práctica lo habitual es entre 6 y 18 meses, con una capa caliente consultable y un archivo más económico, documentando la decisión y su justificación.',
      },
    ],
    related: [
      { label: 'Conformidad NIS2 con LogPulse', href: '/compliance/nis2' },
      { label: 'SIEM de LogPulse', href: '/siem' },
      { label: 'Planes y precios', href: '/pricing' },
      { label: 'NIS2 logging requirements (inglés)', href: '/guides/nis2-logging-requirements' },
    ],
  },
  {
    slug: 'nis2-italia-dlgs-138',
    locale: 'it',
    altOf: 'nis2-logging-requirements',
    title: 'NIS2 in Italia: il D.Lgs. 138/2024 spiegato',
    metaTitle: 'NIS2 in Italia: D.Lgs. 138/2024, ACN e logging | LogPulse',
    description:
      "Cosa significano NIS2 e il Decreto Legislativo 138/2024 per la tua organizzazione: chi rientra nel perimetro, gli obblighi di gestione del rischio e di notifica, la vigilanza dell'ACN, le sanzioni, e cosa richiede in concreto per logging e monitoraggio.",
    excerpt:
      "Chi rientra in NIS2, cosa richiede il D.Lgs. 138/2024 in termini di gestione del rischio e notifica degli incidenti, come funziona la vigilanza dell'ACN, e cosa significa in concreto per logging e monitoraggio.",
    category: 'Compliance',
    readTime: '10 min',
    datePublished: '2026-08-12',
    dateModified: '2026-08-12',
    faq: [
      {
        q: "Cos'è il Decreto Legislativo 138/2024?",
        a: "Il Decreto Legislativo 4 settembre 2024, n. 138 recepisce la direttiva europea NIS2 nell'ordinamento italiano. È stato pubblicato in Gazzetta Ufficiale il 1° ottobre 2024 ed è in vigore dal 18 ottobre 2024. Introduce le categorie di soggetti essenziali e importanti, obblighi di gestione del rischio, una notifica degli incidenti in tre fasi, un obbligo di registrazione presso l'ACN e la responsabilità degli organi di amministrazione. Gli obblighi organizzativi di base sono scaduti il 18 aprile 2026.",
      },
      {
        q: 'La mia organizzazione rientra in NIS2?',
        a: "NIS2 si applica alle medie e grandi organizzazioni (da circa 50 dipendenti o 10 milioni di euro di fatturato annuo) nei settori regolati, da energia, trasporti, sanità e infrastrutture digitali fino a servizi postali, rifiuti, chimica, manifatturiero e fornitori di servizi digitali. Alcune organizzazioni rientrano indipendentemente dalle dimensioni, ad esempio come unico fornitore di un servizio essenziale. Le entità nel perimetro devono registrarsi sul portale dell'ACN; chi entra nel perimetro in seguito si registra nella finestra annuale.",
      },
      {
        q: 'Quali sono le scadenze di notifica degli incidenti sotto NIS2 in Italia?',
        a: "Gli incidenti significativi vanno notificati attraverso il CSIRT Italia, coordinato dall'ACN, in tre fasi conformi alla direttiva: un pre-allarme entro 24 ore, una notifica completa entro 72 ore con una prima valutazione di gravità, impatto e indicatori di compromissione, e una relazione finale entro un mese con causa probabile e misure adottate. Rispettare questi termini presuppone che i log esistano già, siano ricercabili e che il rilevamento abbia notato l'incidente.",
      },
      {
        q: 'Per quanto tempo devo conservare i log sotto NIS2?',
        a: 'Né la direttiva né il decreto italiano fissano un termine di conservazione. Il criterio è basato sul rischio: abbastanza a lungo da poter rilevare, indagare e notificare gli incidenti, inclusa la relazione finale dovuta entro un mese. Nella pratica le organizzazioni adottano 6-18 mesi, spesso divisi tra un livello "hot" ricercabile e un archivio più economico. Documenta la scelta e la motivazione: è ciò che l\'autorità verifica in un audit.',
      },
      {
        q: 'LogPulse rende la mia organizzazione conforme a NIS2?',
        a: 'No, nessun prodotto rende conforme da solo. LogPulse copre però il lato logging, rilevamento e monitoraggio degli obblighi: gestione centralizzata dei log con retention configurabile, rilevamenti in tempo reale con mapping MITRE ATT&CK, workflow di incidente che sostengono le notifiche entro 24 e 72 ore, e report di compliance che collegano rilevamenti e dati agli obblighi NIS2. Tutti i dati restano nell\'UE (GCP Amsterdam).',
      },
    ],
    related: [
      { label: 'Compliance NIS2 con LogPulse', href: '/compliance/nis2' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'NIS2 logging requirements (inglese, approfondimento)', href: '/guides/nis2-logging-requirements' },
      { label: 'Prezzi', href: '/pricing' },
    ],
  },
  {
    slug: 'dora-logging-monitoring-requirements',
    title: 'DORA logging & monitoring requirements',
    metaTitle: 'DORA Logging & Monitoring Requirements (2026) | LogPulse',
    description:
      'What DORA requires for logging and monitoring: detection (Article 10), what to log, retention, and the 4h/72h/1-month major-incident reporting deadlines for financial entities.',
    excerpt:
      'Detection under Article 10, what to log, retention, and the 4h/72h/1-month major-incident reporting clock for financial entities.',
    category: 'Compliance',
    readTime: '9 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'How long must financial entities keep logs under DORA?',
        a: 'DORA sets no single statutory retention period. The ICT risk-management technical standards require you to define the retention period in a logging policy, justified by the events logged and your need to investigate and report incidents. In practice firms keep logs for a multi-month-to-multi-year window, often split into a hot tier and an archive, and frequently align with overlapping regimes such as the GDPR and PCI DSS.',
      },
      {
        q: 'What are the DORA incident reporting deadlines?',
        a: 'For a major ICT-related incident: an initial notification within 4 hours of classifying it as major (and no later than 24 hours after detection), an intermediate report within 72 hours of the initial notification, and a final report within one month of the latest intermediate report. The 4-hour clock starts at classification, not detection.',
      },
      {
        q: 'Does DORA require a SIEM?',
        a: 'DORA does not name any product category, but Article 10 requires continuous monitoring of user activity, anomalies and incidents, multiple layers of control, defined alert thresholds, and automatic alerting that initiates incident response. That is the functional definition of a SIEM with anomaly detection, so most in-scope firms need SIEM-like capability.',
      },
      {
        q: 'When did DORA come into effect?',
        a: 'DORA (Regulation (EU) 2022/2554) has been in application since 17 January 2025. As an EU regulation it applies directly across all Member States, without national transposition.',
      },
      {
        q: 'Who must comply with DORA?',
        a: 'A broad range of financial entities (banks, payment and e-money institutions, investment firms, insurers, crypto-asset service providers, trading venues and more), plus critical ICT third-party providers serving them. ICT suppliers to financial entities typically inherit DORA obligations through contracts.',
      },
    ],
    related: [
      { label: 'DORA compliance with LogPulse', href: '/compliance/dora' },
      { label: 'NIS2 logging requirements', href: '/guides/nis2-logging-requirements' },
      { label: 'Log retention requirements', href: '/guides/log-retention-requirements' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'log-retention-requirements',
    title: 'Log retention requirements by regulation',
    metaTitle: 'Log Retention Requirements by Regulation (2026) | LogPulse',
    description:
      'How long to keep logs under PCI DSS, HIPAA, SOX, GDPR, ISO 27001, NIS2 and DORA: a side-by-side comparison and how to set a defensible retention policy.',
    excerpt:
      'How long to keep logs under PCI DSS, HIPAA, SOX, GDPR, ISO 27001, NIS2 and DORA: side by side, plus how to set a policy.',
    category: 'Compliance',
    readTime: '8 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'How long should you keep logs?',
        a: 'There is no universal answer. Take the strictest retention requirement that applies to you and keep logs at least that long, while not holding personal data longer than the GDPR allows. Most organisations use a tiered policy: a hot tier (recent logs, fast to search) and a cheaper archive tier for the long-tail compliance and forensic window.',
      },
      {
        q: 'What is the minimum log retention for PCI DSS?',
        a: 'PCI DSS v4.0 (Requirement 10) requires retaining audit logs for at least 12 months, with at least the most recent 3 months immediately available for analysis.',
      },
      {
        q: 'Does the GDPR set a log retention period?',
        a: 'No. The GDPR applies the storage-limitation principle: keep personal data only as long as needed for the stated purpose, and justify the period. For security logs containing personal data, organisations commonly retain 1–3 years and redact where possible.',
      },
      {
        q: 'What is the difference between hot and cold (archive) log retention?',
        a: 'The hot tier holds recent logs kept fast to query for live monitoring, alerting and active investigation. The archive (cold) tier holds older logs in cheaper storage to satisfy long compliance windows and reconstruct incidents that surface months later. Splitting the two controls cost while meeting retention.',
      },
      {
        q: 'How do you choose a log retention period?',
        a: 'List every regulation, contract and sector rule that applies, take the strictest binding requirement, add a risk-based buffer for incident detection and forensics, decide your hot/archive split, apply GDPR storage-limitation to personal data, and document the period and its legal/risk basis.',
      },
    ],
    related: [
      { label: 'NIS2 logging requirements', href: '/guides/nis2-logging-requirements' },
      { label: 'DORA logging requirements', href: '/guides/dora-logging-monitoring-requirements' },
      { label: 'ClickHouse log management', href: '/clickhouse-log-management' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'iso-27001-logging-requirements',
    title: 'ISO 27001 logging requirements',
    metaTitle: 'ISO 27001 Logging Requirements (A.8.15) (2026) | LogPulse',
    description:
      'ISO 27001 logging requirements explained: Annex A controls A.8.15 (Logging), A.8.16 (Monitoring) and A.8.17 (Clock synchronisation), what to log, retention, and what auditors expect.',
    excerpt:
      'Annex A controls A.8.15 (Logging), A.8.16 (Monitoring) and A.8.17 (Clock sync): what to log, retention, and what auditors expect.',
    category: 'Compliance',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What does ISO 27001 require for logging?',
        a: 'ISO 27001:2022 covers logging through Annex A controls A.8.15 (Logging: produce, store, protect and analyse logs of relevant events), A.8.16 (Monitoring activities: watch systems for anomalous behaviour), and A.8.17 (Clock synchronisation: sync clocks to an approved time source so timestamps are accurate and correlatable).',
      },
      {
        q: 'How long do you need to keep logs for ISO 27001?',
        a: 'ISO 27001 sets no fixed retention period. You determine it from your risk assessment, legal and contractual obligations, and incident-investigation needs, and document it in your ISMS. A commonly used baseline is at least 12 months; where other regimes apply, take the strictest.',
      },
      {
        q: 'What is ISO 27001 control A.8.17?',
        a: 'A.8.17 (Clock synchronisation) requires the clocks of all relevant information-processing systems to be synchronised to a single approved time source, typically via NTP, so log timestamps are accurate. Accurate time is essential for correlating events across systems during an investigation.',
      },
      {
        q: 'What do auditors look for in ISO 27001 logging?',
        a: 'Evidence that relevant events are logged centrally and protected from tampering, that logs are actively monitored and reviewed (not just stored), that clocks are synchronised (NTP config and drift monitoring), and that a risk-justified retention period is documented in the ISMS.',
      },
    ],
    related: [
      { label: 'Security', href: '/security' },
      { label: 'NIS2 logging requirements', href: '/guides/nis2-logging-requirements' },
      { label: 'Log retention requirements', href: '/guides/log-retention-requirements' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'gdpr-pii-in-logs',
    title: 'GDPR & PII in logs',
    metaTitle: 'GDPR & PII in Logs: Handling Personal Data (2026) | LogPulse',
    description:
      'How the GDPR applies to logs: why logs contain personal data, the principles that bite (minimisation, storage limitation), the right to erasure, and how to redact PII responsibly.',
    excerpt:
      'Why logs contain personal data, the GDPR principles that apply, the right to erasure, and how to redact PII responsibly.',
    category: 'Compliance',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'Does the GDPR apply to logs?',
        a: 'Yes. Logs routinely contain personal data: usernames, user IDs, email addresses, and IP addresses (which EU case law treats as personal data in many contexts). A log store is therefore a store of personal data, and the GDPR’s principles apply to it.',
      },
      {
        q: 'How long can you keep logs under the GDPR?',
        a: 'The GDPR sets no fixed number but applies the storage-limitation principle: keep personal data only as long as needed for the stated purpose, and justify the period. For security logs, network and information security is a recognised legitimate interest (Recital 49), which commonly supports a defined retention window.',
      },
      {
        q: 'How do you handle the right to erasure for logs?',
        a: 'Security logging under a legitimate-interest or legal-obligation basis can justify retaining logs for a defined period despite an erasure request. Pseudonymising or redacting personal data in logs reduces the footprint so erasure is less often required in the first place.',
      },
      {
        q: 'How do you handle PII in logs?',
        a: 'Avoid logging secrets and unnecessary personal data at the source, redact or pseudonymise PII at ingest before storage, set and document a justified retention period, secure the store with access control and encryption, and, where EU law applies, keep log data in the EU.',
      },
    ],
    related: [
      { label: 'Log retention requirements', href: '/guides/log-retention-requirements' },
      { label: 'Structured logging', href: '/guides/structured-logging' },
      { label: 'NIS2 logging requirements', href: '/guides/nis2-logging-requirements' },
      { label: 'Security', href: '/security' },
    ],
  },
  {
    slug: 'what-is-siem',
    title: 'What is SIEM?',
    metaTitle: 'What Is SIEM? Security Information & Event Management (2026) | LogPulse',
    description:
      'SIEM explained: what Security Information and Event Management is, what a SIEM does, how it works, and how the modern AI-era SIEM (UEBA, SOAR, RBA) has evolved.',
    excerpt:
      'What Security Information and Event Management is, what it does, how it works, and how the modern AI-era SIEM has evolved.',
    category: 'Security',
    readTime: '9 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What does SIEM stand for?',
        a: 'SIEM stands for Security Information and Event Management. It combines two earlier technologies: Security Information Management (SIM), for log storage and reporting, and Security Event Management (SEM), for real-time monitoring and alerting. The term was coined by Gartner analysts in 2005.',
      },
      {
        q: 'What is the difference between SIM and SEM?',
        a: 'SIM (Security Information Management) focuses on long-term storage, analysis and reporting of log data, the compliance and forensics side. SEM (Security Event Management) focuses on real-time monitoring and alerting, the live detection side. A SIEM is the combination of both on one platform.',
      },
      {
        q: 'What does a SIEM do?',
        a: 'A SIEM collects and normalises logs and security telemetry from across your environment, correlates events to detect threats, raises prioritised alerts, gives analysts fast search and context to investigate and respond (TDIR), and retains the data for forensics and compliance reporting.',
      },
      {
        q: 'What is the difference between SIEM and log management?',
        a: 'Log management focuses on collecting, storing and searching logs. A SIEM adds the security layer on top: correlation, threat detection, risk-based alerting, behavioral analytics (UEBA), and incident response, so it is purpose-built for finding and responding to attacks, not just storing logs.',
      },
      {
        q: 'Do I need a SIEM for compliance?',
        a: 'Frameworks like NIS2 and DORA rarely name SIEM outright, but they require continuous monitoring, correlation, alerting, retention and incident reporting, requirements that are very hard to meet at scale without SIEM-like capability.',
      },
    ],
    related: [
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'What is risk-based alerting?', href: '/guides/what-is-risk-based-alerting' },
      { label: 'What is UEBA?', href: '/guides/what-is-ueba' },
      { label: 'What is SOAR?', href: '/guides/what-is-soar' },
      { label: 'Detection-as-code', href: '/guides/detection-as-code' },
      { label: 'What is an agentic SOC?', href: '/guides/what-is-an-agentic-soc' },
    ],
  },
  {
    slug: 'what-is-risk-based-alerting',
    title: 'What is risk-based alerting (RBA)?',
    metaTitle: 'What Is Risk-Based Alerting (RBA)? (2026) | LogPulse',
    description:
      'Risk-based alerting (RBA) explained: how attributing risk to entities instead of firing an alert per rule cuts alert fatigue by 50–90% and surfaces real attacks.',
    excerpt:
      'How attributing risk to entities, instead of one alert per rule, cuts alert fatigue and surfaces the attacks that matter.',
    category: 'Security',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is risk-based alerting?',
        a: 'Risk-based alerting (RBA) is a SIEM approach where detections do not alert directly. Each detection emits a risk event attributed to an entity (user, host, IP) with a score; risk accumulates on the entity, and only when it crosses a threshold is a single notable case raised, carrying all the contributing context.',
      },
      {
        q: 'How does risk-based alerting reduce alert fatigue?',
        a: 'Because cases are raised per entity at a threshold rather than per rule match, RBA collapses thousands of low-fidelity alerts into a handful of high-confidence notables. Industry implementations report cutting low-fidelity alert volume by roughly 50–90%.',
      },
      {
        q: 'What is a risk object and a risk score?',
        a: 'A risk object is the entity that risk is attributed to, typically a user, host, or IP. The risk score is the accumulated risk on that entity. A good score uses diminishing returns so repeats saturate, an attack-breadth boost so activity across more MITRE stages weighs more, and time decay so old noise fades.',
      },
      {
        q: 'How is RBA different from traditional SIEM alerting?',
        a: 'Traditional alerting fires one alert per rule match, so volume equals importance and analysts drown. RBA attributes risk to entities, accumulates context across signals and MITRE tactics, and promotes one notable per entity at a single threshold, rewarding attack breadth over repetitive noise.',
      },
    ],
    related: [
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'What is an agentic SOC?', href: '/guides/what-is-an-agentic-soc' },
      { label: 'Agentic SOC', href: '/agentic-soc' },
    ],
  },
  {
    slug: 'what-is-an-agentic-soc',
    title: 'What is an agentic SOC?',
    metaTitle: 'What Is an Agentic SOC? (2026) | LogPulse',
    description:
      'Agentic SOC explained: AI agents that plan and run investigations, how it differs from SOAR, the human-in-the-loop model, and why governance is what matters.',
    excerpt:
      'AI agents that plan and run investigations: how it differs from SOAR, and why human-in-the-loop governance is the part that matters.',
    category: 'AI & Agents',
    readTime: '8 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is an agentic SOC?',
        a: 'An agentic SOC is a security-operations model where AI agents can perceive context, plan a multi-step investigation, make decisions, and take or recommend actions toward a goal under human-defined constraints, rather than only answering a prompt or scoring an alert.',
      },
      {
        q: 'How is an agentic SOC different from SOAR?',
        a: 'SOAR and classic automation follow a fixed, pre-written decision tree. Agentic systems plan dynamically: they adjust the investigation path based on what they find and reason across multiple data sources in sequence, producing a reasoned verdict with evidence rather than just an action when conditions match.',
      },
      {
        q: 'Does an agentic SOC replace analysts?',
        a: 'No. The 2026 consensus is augmentation, not replacement, often called human-on-the-loop: AI handles alert volume at machine speed while humans own judgment, business context, novel threats, and consequential actions. Analysts shift from triaging alerts to supervising agent-led investigations.',
      },
      {
        q: 'What does "human in the loop" mean in an agentic SOC?',
        a: 'It means agents can investigate and draft changes freely, but any action that modifies your environment is gated behind human approval. Transparency, citations and an auditable trail, is what makes that supervision possible, and it is especially important for regulated teams.',
      },
    ],
    related: [
      { label: 'Agentic SOC', href: '/agentic-soc' },
      { label: 'What is risk-based alerting?', href: '/guides/what-is-risk-based-alerting' },
      { label: 'MCP for security', href: '/guides/mcp-for-security' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'mcp-for-security',
    title: 'MCP for security: connecting AI agents to your logs',
    metaTitle: 'MCP for Security: Connect AI Agents to Logs (2026) | LogPulse',
    description:
      'Model Context Protocol (MCP) for security data: what MCP is, the risks (prompt injection, tool poisoning, over-broad scopes), and how to connect AI agents to logs safely.',
    excerpt:
      'What MCP is, the real risks (prompt injection, tool poisoning, over-broad scopes), and how to connect AI agents to your logs safely.',
    category: 'AI & Agents',
    readTime: '8 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is the Model Context Protocol (MCP)?',
        a: 'MCP is an open protocol that standardises how AI agents connect to external tools and data. An agent connects to an MCP server, discovers a catalog of typed tools, and calls them to read data or take actions, instead of scraping a UI or hand-writing queries. It is becoming the common interface between agents like Claude Code, Cursor and Codex and the systems they work with.',
      },
      {
        q: 'Is it safe to connect an AI agent to your logs over MCP?',
        a: 'It can be, if you follow least-privilege and treat data as untrusted. Connect read-only by default, scope access per token, enforce existing RBAC, route through an audited gateway with rate limits, require human approval for any change, and treat tool output (including log lines) as data, not instructions.',
      },
      {
        q: 'What are tool poisoning and prompt injection in MCP?',
        a: 'Prompt injection is malicious instructions hidden in data the agent reads (including your own logs) that try to hijack its behaviour. Tool poisoning embeds malicious instructions in a tool’s metadata or description, manipulating the agent through a trusted interface. Both are mitigated by treating retrieved content as data and constraining what tools can do.',
      },
      {
        q: 'How does LogPulse secure MCP access?',
        a: 'LogPulse’s remote MCP server is read-only by default, authenticated by a scoped personal access token or OAuth 2.1, bound by the user’s namespace RBAC, rate-limited and fully audited. The only writes are opt-in propose scopes that queue disabled drafts for human approval, tool output is treated as data to guard against prompt injection, and data stays EU-hosted.',
      },
    ],
    related: [
      { label: 'MCP server', href: '/mcp' },
      { label: 'Close the loop for agent-built apps', href: '/close-the-loop' },
      { label: 'Production logs in your AI IDE', href: '/guides/production-logs-in-your-ai-ide' },
      { label: 'What is an agentic SOC?', href: '/guides/what-is-an-agentic-soc' },
      { label: 'MCP documentation', href: '/docs/integrations/mcp-server' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'production-logs-in-your-ai-ide',
    title: 'Closing the loop: production logs in your AI IDE',
    metaTitle: 'Production Logs in Your AI IDE via MCP (2026) | LogPulse',
    description:
      'Connect Claude Code, Cursor or Codex to production logs over MCP: verify deploys, debug real errors and close the loop between writing and running code.',
    excerpt:
      'Why AI-assisted development needs production feedback, and how MCP lets the agent that wrote the code verify deploys and debug errors from the IDE.',
    category: 'AI & Agents',
    readTime: '8 min',
    datePublished: '2026-07-10',
    dateModified: '2026-07-10',
    faq: [
      {
        q: 'Can Claude Code read my production logs?',
        a: 'Yes. Connect Claude Code to a log platform that ships a remote MCP server, such as LogPulse, and it can call typed tools like search_logs and count_patterns directly. The agent queries production logs, error patterns and service health from inside the coding session, read-only by default and scoped to your token.',
      },
      {
        q: 'How do I connect Cursor or Codex to my logs?',
        a: 'The same way as any MCP client: add the log platform’s MCP endpoint to the agent’s MCP configuration and authenticate with one-click OAuth or a scoped personal access token. LogPulse exposes one HTTPS endpoint (https://api.logpulse.io/mcp) that works with Claude Code, Cursor, Codex and any other MCP-capable agent.',
      },
      {
        q: 'What does "closing the loop" mean in AI-assisted development?',
        a: 'It means the AI agent that writes the code also sees the code run. After a deploy it queries production logs for new errors, reads the real stack traces, writes the fix and verifies in the logs that the errors are gone. The feedback cycle that used to run through dashboards and bug reports happens inside the coding session, in minutes.',
      },
      {
        q: 'Is it safe to give a coding agent access to production logs?',
        a: 'It can be, with the right guardrails: read-only access by default, per-token scopes, the user’s existing RBAC enforced on every query, rate limits, a full audit trail, and human approval for anything the agent proposes to change. Log content should be treated as data rather than instructions, to mitigate prompt injection.',
      },
      {
        q: 'How do I debug an app built with vibe coding?',
        a: 'Give the agent that built the app access to its production logs. Add structured logging in the first session, ship the logs to a platform the agent can query over MCP, and ask the agent why the app is failing: it reads the actual errors, sees when they started and which deploy caused them, and fixes the code it already knows.',
      },
    ],
    related: [
      { label: 'MCP server', href: '/mcp' },
      { label: 'Close the loop for agent-built apps', href: '/close-the-loop' },
      { label: 'MCP for security', href: '/guides/mcp-for-security' },
      { label: 'MCP documentation', href: '/docs/integrations/mcp-server' },
      { label: 'AI root cause analysis', href: '/guides/ai-root-cause-analysis' },
      { label: 'Quickstart: ship your first log', href: '/docs/getting-started/quickstart' },
    ],
  },
  {
    slug: 'productielogs-in-je-ai-ide',
    locale: 'nl',
    altOf: 'production-logs-in-your-ai-ide',
    title: 'De loop sluiten: productielogs in je AI-IDE',
    metaTitle: 'Productielogs in je AI-IDE via MCP (2026) | LogPulse',
    description:
      'Koppel Claude Code, Cursor of Codex via MCP aan je productielogs: verifieer deploys, debug echte fouten en sluit de loop tussen code schrijven en code draaien.',
    excerpt:
      'Waarom AI-ontwikkeling productiefeedback nodig heeft, en hoe MCP de agent die de code schreef deploys laat verifiëren en fouten laat debuggen vanuit de IDE.',
    category: 'AI & Agents',
    readTime: '8 min',
    datePublished: '2026-07-10',
    dateModified: '2026-07-10',
    faq: [
      {
        q: 'Kan Claude Code mijn productielogs lezen?',
        a: 'Ja. Verbind Claude Code met een logplatform dat een remote MCP-server heeft, zoals LogPulse, en het kan getypeerde tools als search_logs en count_patterns rechtstreeks aanroepen. De agent bevraagt productielogs, foutpatronen en service-health vanuit de codeersessie, standaard read-only en begrensd tot de scopes van je token.',
      },
      {
        q: 'Hoe koppel ik Cursor of Codex aan mijn logs?',
        a: 'Zoals elke MCP-client: voeg het MCP-endpoint van het logplatform toe aan de MCP-configuratie van de agent en authenticeer met one-click OAuth of een gescopet personal access token. LogPulse biedt één HTTPS-endpoint (https://api.logpulse.io/mcp) dat werkt met Claude Code, Cursor, Codex en elke andere MCP-agent.',
      },
      {
        q: 'Wat betekent "de loop sluiten" bij AI-ondersteund ontwikkelen?',
        a: 'Het betekent dat de AI-agent die de code schrijft de code ook ziet draaien. Na een deploy bevraagt hij de productielogs op nieuwe fouten, leest de echte stacktraces, schrijft de fix en verifieert in de logs dat de fouten weg zijn. De feedbackcyclus die vroeger via dashboards en bugmeldingen liep, gebeurt binnen de codeersessie, in minuten.',
      },
      {
        q: 'Is het veilig om een coding-agent toegang te geven tot productielogs?',
        a: 'Dat kan, met de juiste guardrails: standaard read-only toegang, scopes per token, de bestaande RBAC van de gebruiker afgedwongen op elke query, rate limits, een volledige audittrail, en menselijke goedkeuring voor alles wat de agent wil wijzigen. Behandel loginhoud als data en niet als instructies, om prompt injection af te vangen.',
      },
      {
        q: 'Hoe debug ik een app die met vibe coding gebouwd is?',
        a: 'Geef de agent die de app bouwde toegang tot de productielogs van die app. Voeg gestructureerde logging toe in de eerste sessie, stuur de logs naar een platform dat de agent via MCP kan bevragen, en vraag de agent waarom de app faalt: hij leest de echte fouten, ziet wanneer ze begonnen en welke deploy ze veroorzaakte, en fixt de code die hij al kent.',
      },
    ],
    related: [
      { label: 'MCP-server', href: '/mcp' },
      { label: 'Close the loop voor apps van coding agents', href: '/close-the-loop' },
      { label: 'MCP for security', href: '/guides/mcp-for-security' },
      { label: 'MCP-documentatie', href: '/docs/integrations/mcp-server' },
      { label: 'AI root cause analysis', href: '/guides/ai-root-cause-analysis' },
      { label: 'Quickstart: je eerste log in 5 minuten', href: '/docs/getting-started/quickstart' },
    ],
  },
  {
    slug: 'what-is-ueba',
    title: 'What is UEBA?',
    metaTitle: 'What Is UEBA? User & Entity Behavior Analytics (2026) | LogPulse',
    description:
      'UEBA explained: how User and Entity Behavior Analytics baselines users and systems, detects anomalies like compromised accounts and insider threats, and feeds a SIEM.',
    excerpt:
      'How behavioral baselines for users and entities catch compromised accounts, insider threats and lateral movement that static rules miss.',
    category: 'Security',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is UEBA?',
        a: 'UEBA (User and Entity Behavior Analytics) is a security technology that uses statistical analysis and machine learning to build a behavioral baseline for users and entities, then detects anomalies that may indicate a threat, asking whether behaviour is normal for this user or system, rather than whether it matched a known-bad rule.',
      },
      {
        q: 'What does the "entity" in UEBA mean?',
        a: 'An entity is a non-user actor: a host, server, application, service account, or IP address. Modelling entities as well as users lets UEBA catch threats with no human at the keyboard, such as a compromised service account or a server beaconing to a command-and-control host.',
      },
      {
        q: 'How does UEBA work?',
        a: 'UEBA baselines each user’s and entity’s normal pattern (login times, locations, data volumes, peer-group behaviour), compares current activity against that baseline accounting for daily and weekly rhythms, scores how abnormal the behaviour is, and attributes that risk to the user or entity.',
      },
      {
        q: 'How is UEBA different from a SIEM?',
        a: 'A SIEM collects and correlates evidence and runs detection rules; UEBA adds the behavioral layer, turning activity into a risk signal based on what is normal for each entity. UEBA is most useful as part of a SIEM, where it feeds risk-based alerting alongside detections and threat intel.',
      },
    ],
    related: [
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'What is SIEM?', href: '/guides/what-is-siem' },
      { label: 'What is risk-based alerting?', href: '/guides/what-is-risk-based-alerting' },
    ],
  },
  {
    slug: 'what-is-soar',
    title: 'What is SOAR?',
    metaTitle: 'What Is SOAR? Security Orchestration, Automation & Response (2026) | LogPulse',
    description:
      'SOAR explained: Security Orchestration, Automation and Response, the three pillars, what a playbook is, how SOAR differs from SIEM, and when to gate automation behind a human.',
    excerpt:
      'Security Orchestration, Automation and Response: the three pillars, playbooks, SOAR vs SIEM, and where the human stays in control.',
    category: 'Security',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What does SOAR stand for?',
        a: 'SOAR stands for Security Orchestration, Automation and Response. It helps security teams act on incidents by connecting tools (orchestration), automating repetitive response steps (automation), and managing the incident workflow end to end (response). Where a SIEM detects, SOAR acts.',
      },
      {
        q: 'What is a SOAR playbook?',
        a: 'A playbook is a defined sequence of response steps that runs when a trigger fires: for example enrich the alert, open a ticket, notify the on-call analyst, and optionally contain the threat. Playbooks make response fast, consistent, and well-documented.',
      },
      {
        q: 'What is the difference between SIEM and SOAR?',
        a: 'A SIEM detects: it collects, correlates and alerts on security data. SOAR responds: it orchestrates tools, automates response steps, and manages cases, usually acting on alerts from the SIEM. Modern SIEMs increasingly embed SOAR-style response rather than requiring a separate product.',
      },
      {
        q: 'Is automated response safe?',
        a: 'Automating low-risk, reversible steps (enrich, ticket, notify) is safe. Destructive actions (disabling a user, blocking traffic, isolating a host) can disrupt the business on a false positive, so a sound default is to automate enrichment and gate destructive actions behind human approval, especially when AI agents drive the response.',
      },
    ],
    related: [
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'What is SIEM?', href: '/guides/what-is-siem' },
      { label: 'What is an agentic SOC?', href: '/guides/what-is-an-agentic-soc' },
      { label: 'Agentic SOC', href: '/agentic-soc' },
    ],
  },
  {
    slug: 'detection-as-code',
    title: 'What is detection-as-code?',
    metaTitle: 'What Is Detection-as-Code? Detection Engineering (2026) | LogPulse',
    description:
      'Detection-as-code explained: managing SIEM detections like software (Git version control, peer review, CI/CD testing, Sigma rules) for higher-quality, auditable detections.',
    excerpt:
      'Managing detections like software (Git, peer review, CI/CD testing and Sigma) for higher-quality, auditable, reversible detection content.',
    category: 'Security',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is detection-as-code?',
        a: 'Detection-as-code (DaC) treats detection rules as software artifacts: rules live as files in a version-controlled Git repository, changes go through peer-reviewed pull requests, automated tests in CI/CD verify they fire correctly, and deployment happens through a pipeline rather than manually in a console.',
      },
      {
        q: 'What is Sigma in detection engineering?',
        a: 'Sigma is an open-source, vendor-agnostic signature format for SIEM detections. You write the detection logic once and convert it to the native query language of your platform (SPL, KQL, Elastic DSL, LPQL and others). Being text-based and portable makes Sigma a natural fit for detection-as-code.',
      },
      {
        q: 'Why use detection-as-code?',
        a: 'It improves rule quality through peer review, gives full change tracking via Git history, enables fast rollback of a problematic rule, lets you replicate a vetted detection set across environments, and turns detection engineering into a collaborative practice with an auditable trail.',
      },
      {
        q: 'How does detection-as-code relate to a SIEM?',
        a: 'Detection-as-code is the delivery workflow for the detections your SIEM runs. Instead of editing rules by hand in the SIEM console, you author, version, test and deploy them as code, then push them to the SIEM programmatically.',
      },
    ],
    related: [
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'What is SIEM?', href: '/guides/what-is-siem' },
      { label: 'MCP for security', href: '/guides/mcp-for-security' },
      { label: 'What is an agentic SOC?', href: '/guides/what-is-an-agentic-soc' },
    ],
  },
  {
    slug: 'what-is-log-management',
    title: 'What is log management?',
    metaTitle: 'What Is Log Management? (2026) | LogPulse',
    description:
      'Log management explained: how collecting, parsing, storing, searching and retaining logs works, how it relates to observability and SIEM, and the common challenges.',
    excerpt:
      'How collecting, parsing, storing, searching and retaining logs works, and how it relates to observability and SIEM.',
    category: 'Log management',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is log management?',
        a: 'Log management is the end-to-end handling of log data: collecting logs from across your estate, parsing them into a usable structure, storing them efficiently, making them searchable, alerting on patterns, and retaining them for as long as operations and compliance require.',
      },
      {
        q: 'What is the difference between log management and a SIEM?',
        a: 'Log management focuses on collecting, storing, searching and retaining logs. A SIEM adds the security layer on top: correlation, threat detection, risk-based alerting, behavioral analytics and incident response, so it is purpose-built for finding and responding to attacks, not just storing logs.',
      },
      {
        q: 'Is log management the same as observability?',
        a: 'No. Observability is about understanding system health and performance, usually across logs, metrics and traces. Log management is specifically the handling of logs, and it is the foundation that both observability and SIEM build on.',
      },
      {
        q: 'What is centralized log management?',
        a: 'Centralized log management collects logs from many sources into one place so they can be searched and correlated together, instead of sitting in silos on individual systems. It is a prerequisite for effective troubleshooting, monitoring and security investigation.',
      },
    ],
    related: [
      { label: 'ClickHouse log management', href: '/clickhouse-log-management' },
      { label: 'What is SIEM?', href: '/guides/what-is-siem' },
      { label: 'SIEM vs log management vs XDR', href: '/guides/siem-vs-log-management-vs-xdr' },
      { label: 'AI log search', href: '/ai-log-search' },
    ],
  },
  {
    slug: 'siem-vs-log-management-vs-xdr',
    title: 'SIEM vs log management vs XDR',
    metaTitle: 'SIEM vs Log Management vs XDR: The Difference (2026) | LogPulse',
    description:
      'SIEM vs log management vs XDR explained: what each does, how they differ on data scope, retention and response, and which one you actually need.',
    excerpt:
      'What each does, how they differ on data scope, retention and response, and which one you actually need.',
    category: 'Log management',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is the difference between SIEM and log management?',
        a: 'Log management collects, stores, searches and retains logs for everyone (ops, dev, security). A SIEM adds a security layer (correlation, threat detection, risk-based alerting and incident response) and long retention for compliance and forensics.',
      },
      {
        q: 'What is the difference between SIEM and XDR?',
        a: 'A SIEM is log- and compliance-centric: broad visibility, correlation, long retention, and investigation across your whole IT estate. XDR is threat- and response-centric: it unifies endpoint, network and cloud telemetry for fast, often automated detection and response, usually with shorter retention.',
      },
      {
        q: 'Do I need all three?',
        a: 'Not necessarily. Most organisations need log management as the base and add SIEM capability for security and compliance. XDR is a choice for teams that want integrated, automated detection and response across endpoints and network. The categories increasingly converge onto one engine.',
      },
      {
        q: 'Which is best for compliance?',
        a: 'SIEM and log management, because frameworks like NIS2 and DORA require long retention, correlation, monitoring and incident reporting. XDR’s shorter telemetry retention makes it less suited to long-term compliance on its own.',
      },
    ],
    related: [
      { label: 'What is SIEM?', href: '/guides/what-is-siem' },
      { label: 'What is log management?', href: '/guides/what-is-log-management' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
      { label: 'Compare', href: '/compare' },
    ],
  },
  {
    slug: 'clickhouse-vs-elasticsearch-logs',
    title: 'ClickHouse vs Elasticsearch for logs',
    metaTitle: 'ClickHouse vs Elasticsearch for Logs (2026) | LogPulse',
    description:
      'ClickHouse vs Elasticsearch for log analytics: columnar vs inverted index, 5–10× compression, faster aggregations, far lower cost, and where Elasticsearch still wins.',
    excerpt:
      'Columnar vs inverted index: compression, query speed and cost for log analytics, and where Elasticsearch still wins.',
    category: 'Log management',
    readTime: '8 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'Is ClickHouse better than Elasticsearch for logs?',
        a: 'For log analytics (high-volume, time-series, filter-and-aggregate workloads) ClickHouse is usually better: a columnar engine that compresses logs 5–10× and runs aggregation queries far faster, at much lower cost. Elasticsearch remains strong for full-text relevance search.',
      },
      {
        q: 'Why is ClickHouse so much cheaper than Elasticsearch for logs?',
        a: 'Elasticsearch stores each document multiple times (inverted index, stored fields, doc-values) and achieves modest compression, so it typically uses an order of magnitude more disk than ClickHouse, which uses columnar layout with strong (ZSTD) compression. Less storage and lower CPU overhead mean far fewer nodes for the same data.',
      },
      {
        q: 'Does ClickHouse do full-text search?',
        a: 'ClickHouse handles log search and filtering very well and has text-search capabilities, but Elasticsearch’s inverted index is purpose-built for full-text relevance ranking. If your workload is document/relevance search rather than log analytics, Elasticsearch may fit better.',
      },
      {
        q: 'Should I migrate from Elasticsearch to ClickHouse for logs?',
        a: 'If log analytics cost and query speed at scale are your pain points, ClickHouse is the common destination. The trade-off is full-text relevance features; for most log workloads (filter, aggregate, trend) the columnar engine wins on cost and speed.',
      },
    ],
    related: [
      { label: 'ClickHouse log management', href: '/clickhouse-log-management' },
      { label: 'What is log management?', href: '/guides/what-is-log-management' },
      { label: 'Compare', href: '/compare' },
    ],
  },
  {
    slug: 'lpql-vs-spl',
    title: 'LPQL vs SPL (Splunk query language)',
    metaTitle: 'LPQL vs SPL (Splunk Query Language) (2026) | LogPulse',
    description:
      'LPQL vs SPL: the two share a pipe-based model (stats, eval, sort), so Splunk queries translate easily. The difference is the engine, command breadth, JSON paths and AI generation.',
    excerpt:
      'Both share a pipe-based model, so Splunk queries translate easily. The difference is the engine, command breadth and AI generation.',
    category: 'Log management',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'Is LPQL like SPL?',
        a: 'Yes. LPQL uses the same pipe-based model as Splunk’s SPL: a search expression narrows events, then results are piped through commands like stats, eval, where, top, sort and head. The everyday syntax is nearly identical, which makes moving between them easy.',
      },
      {
        q: 'Can I migrate Splunk SPL queries to LPQL?',
        a: 'Most everyday SPL searches translate to LPQL with little or no change because the filter-pipe-aggregate skeleton is the same. The work is in the long tail of SPL-specific commands and macros with no direct equivalent. Natural-language search lowers that cost further by generating the LPQL for you.',
      },
      {
        q: 'What is different between LPQL and SPL?',
        a: 'LPQL compiles to optimised ClickHouse SQL for columnar speed, focuses on a curated set of around two dozen pipe commands (versus SPL’s vast command set), has first-class JSON-path navigation for structured logs, and can be generated from natural language by the AI Investigator.',
      },
      {
        q: 'Does LPQL support stats and eval like SPL?',
        a: 'Yes. LPQL includes stats, timechart, eval, where, table, top/rare, dedup, sort, head/tail, fields, rename and rex, plus a similar eval-function set (string, math, conditional, time and pattern functions).',
      },
    ],
    related: [
      { label: 'AI log search', href: '/ai-log-search' },
      { label: 'What is log management?', href: '/guides/what-is-log-management' },
      { label: 'LPQL syntax reference', href: '/docs/search/lpql-syntax' },
      { label: 'Compare', href: '/compare' },
      { label: 'Splunk ITSI explained', href: '/guides/splunk-itsi-explained' },
    ],
  },
  {
    slug: 'splunk-itsi-explained',
    title: 'Splunk ITSI explained: services, KPIs and health scores',
    metaTitle: 'Splunk ITSI Explained, Costs & Alternative (2026) | LogPulse',
    description:
      'How Splunk IT Service Intelligence (ITSI) works: services, KPI searches, adaptive thresholds, health scores and episodes. What it costs to run, and a lighter alternative.',
    excerpt:
      'How Splunk ITSI turns searches into KPIs and health scores, what it takes to run, and how the same model works on LogPulse Service Intelligence.',
    category: 'Log management',
    readTime: '9 min',
    datePublished: '2026-10-01',
    dateModified: '2026-10-01',
    faq: [
      {
        q: 'What is Splunk ITSI?',
        a: 'Splunk IT Service Intelligence (ITSI) is a premium app on top of Splunk Enterprise or Splunk Cloud for service monitoring. It models services, turns scheduled searches into KPIs, compares them with static, time-based or adaptive thresholds, rolls them up into a 0–100 health score per service, and groups alerts into episodes.',
      },
      {
        q: 'Is Splunk ITSI included in Splunk Enterprise?',
        a: 'No. ITSI is licensed separately as a premium app on top of Splunk Enterprise or Splunk Cloud. Splunk does not publish list prices for it.',
      },
      {
        q: 'What is a KPI base search in ITSI?',
        a: 'A KPI base search is a shared scheduled search that several KPIs build on. It reads the relevant events once, and each KPI aggregates a value from it, such as an error rate or p95 latency, at a fixed interval.',
      },
      {
        q: 'What is an alternative to Splunk ITSI?',
        a: 'Alternatives range from full observability suites to lighter service-monitoring tools. LogPulse Service Intelligence offers services, KPIs written in LPQL (a Splunk-like query language), seasonal anomaly baselines per KPI, dependency graphs and SLOs with burn-rate alerts, on every plan including Free. It has no glass tables, weighted health score, aggregation-policy engine or predictive health score.',
      },
      {
        q: 'Can I reuse my ITSI KPI searches?',
        a: 'Mostly. LPQL shares SPL’s pipe syntax, including stats with count(eval(...)), so most KPI base searches carry over with small changes. Splunk macros and commands without an LPQL equivalent need rewriting.',
      },
    ],
    related: [
      { label: 'Service Intelligence', href: '/service-intelligence' },
      { label: 'LPQL vs SPL', href: '/guides/lpql-vs-spl' },
      { label: 'Service Intelligence documentation', href: '/docs/observability/service-intelligence' },
      { label: 'Compare platforms', href: '/compare' },
    ],
  },
  {
    slug: 'splunk-itsi-uitgelegd',
    locale: 'nl',
    altOf: 'splunk-itsi-explained',
    title: 'Splunk ITSI uitgelegd: services, KPI\'s en health scores',
    metaTitle: 'Splunk ITSI uitgelegd, kosten en alternatief (2026) | LogPulse',
    description:
      'Hoe Splunk IT Service Intelligence (ITSI) werkt: services, KPI-zoekopdrachten, adaptieve drempels, health scores en episodes. Wat het kost, en een lichter alternatief.',
    excerpt:
      'Hoe Splunk ITSI zoekopdrachten omzet in KPI\'s en health scores, wat het kost om te draaien, en hoe hetzelfde model werkt in LogPulse Service Intelligence.',
    category: 'Log management',
    readTime: '9 min',
    datePublished: '2026-10-01',
    dateModified: '2026-10-01',
    faq: [
      {
        q: 'Wat is Splunk ITSI?',
        a: 'Splunk IT Service Intelligence (ITSI) is een premium-app bovenop Splunk Enterprise of Splunk Cloud voor servicemonitoring. Hij modelleert services, maakt van geplande zoekopdrachten KPI’s, vergelijkt die met statische, tijdgebonden of adaptieve drempels, rolt ze op tot een health score van 0 tot 100 per service en bundelt meldingen in episodes.',
      },
      {
        q: 'Zit Splunk ITSI in Splunk Enterprise?',
        a: 'Nee. ITSI heeft een eigen premium-licentie bovenop Splunk Enterprise of Splunk Cloud. Splunk publiceert er geen lijstprijzen voor.',
      },
      {
        q: 'Wat is een KPI base search in ITSI?',
        a: 'Een KPI base search is een gedeelde geplande zoekopdracht waar meerdere KPI’s op bouwen. Hij leest de relevante events één keer, en elke KPI haalt er met een vast interval een waarde uit, zoals een foutpercentage of p95-latency.',
      },
      {
        q: 'Wat is een alternatief voor Splunk ITSI?',
        a: 'Alternatieven lopen van complete observability-suites tot lichtere tools voor servicemonitoring. LogPulse Service Intelligence biedt services, KPI’s in LPQL (een Splunk-achtige querytaal), seizoensgebonden anomaliebaselines per KPI, afhankelijkheidsgrafen en SLO’s met burn-rate-alerts, in elk plan inclusief Free. Het heeft geen glass tables, gewogen health score, engine voor aggregatiebeleid of voorspellende health score.',
      },
      {
        q: 'Kan ik mijn ITSI-KPI-zoekopdrachten hergebruiken?',
        a: 'Grotendeels. LPQL deelt de pipe-syntaxis van SPL, inclusief stats met count(eval(...)), dus de meeste KPI-zoekopdrachten gaan met kleine aanpassingen mee. Splunk-macro’s en commando’s zonder LPQL-tegenhanger moet je herschrijven.',
      },
    ],
    related: [
      { label: 'Service Intelligence', href: '/service-intelligence' },
      { label: 'LPQL vs SPL', href: '/guides/lpql-vs-spl' },
      { label: 'Documentatie Service Intelligence', href: '/docs/observability/service-intelligence' },
      { label: 'Platforms vergelijken', href: '/compare' },
    ],
  },
  {
    slug: 'structured-logging',
    title: 'Structured logging best practices',
    metaTitle: 'Structured Logging Best Practices (2026) | LogPulse',
    description:
      'Structured logging explained: what it is, why JSON logs beat free-form text, and the best practices (consistent schema, levels, context, correlation IDs, PII handling).',
    excerpt:
      'What it is, why JSON beats free-form text, and the practices (schema, levels, context, correlation IDs, PII) that make it pay off.',
    category: 'Log management',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is structured logging?',
        a: 'Structured logging records each event as machine-readable data, typically JSON key-value pairs, instead of free-form text. The information is the same, but fields like user_id or src_ip can be filtered, grouped and aggregated directly, without fragile text parsing.',
      },
      {
        q: 'What is the difference between structured and unstructured logging?',
        a: 'Unstructured logs are free-form sentences: easy to write and read, but hard to parse and aggregate. Structured logs put data in named fields, so they are reliably queryable, alertable and cheaper to store and search in a columnar engine. The cost is a little discipline at write time.',
      },
      {
        q: 'What format should structured logs use?',
        a: 'JSON is the de facto standard, supported by most logging libraries and collectors. Standards like OpenTelemetry (OTLP) push structured, consistently-attributed telemetry as the default, a good direction to align with.',
      },
      {
        q: 'What are structured logging best practices?',
        a: 'Use a consistent field schema across services, log in JSON, use proper severity levels, add context fields (service, environment, version), include correlation/trace IDs, use accurate UTC timestamps, keep message strings stable with variable data in fields, and avoid or redact PII.',
      },
    ],
    related: [
      { label: 'AI log search', href: '/ai-log-search' },
      { label: 'What is log management?', href: '/guides/what-is-log-management' },
      { label: 'Agent Fleet', href: '/agents' },
    ],
  },
  {
    slug: 'what-is-mitre-attack',
    title: 'What is MITRE ATT&CK?',
    metaTitle: 'What Is MITRE ATT&CK? Tactics & Techniques (2026) | LogPulse',
    description:
      'MITRE ATT&CK explained: the knowledge base of adversary tactics, techniques and sub-techniques, the Enterprise matrix, and how security teams use it for detection and coverage.',
    excerpt:
      'The knowledge base of adversary tactics and techniques, the Enterprise matrix, and how teams use it for detection and coverage.',
    category: 'Security',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is MITRE ATT&CK?',
        a: 'MITRE ATT&CK (Adversarial Tactics, Techniques, and Common Knowledge) is a free, globally-used knowledge base of real-world adversary behaviour maintained by the non-profit MITRE. It catalogues how attackers behave rather than malware signatures, which makes it durable as specific tools change.',
      },
      {
        q: 'What is the difference between a tactic and a technique?',
        a: 'A tactic is the attacker’s goal, the "why" of an action, such as Initial Access or Exfiltration. A technique is the "how", the method used to achieve a tactic, such as Phishing or Valid Accounts (T1078). Sub-techniques are more specific variations of a technique.',
      },
      {
        q: 'How many tactics are in the MITRE ATT&CK Enterprise matrix?',
        a: 'The Enterprise matrix has 14 tactics that roughly follow an attack’s progression, from Reconnaissance and Initial Access through Persistence, Privilege Escalation and Lateral Movement to Command and Control, Exfiltration and Impact.',
      },
      {
        q: 'How do security teams use MITRE ATT&CK?',
        a: 'Teams tag detections with the techniques they catch, map their detection coverage to find gaps, describe threat actors by the techniques they use, plan red/blue team exercises, and prioritise: an attack spanning many tactics is more serious than one noisy technique repeated.',
      },
    ],
    related: [
      { label: 'What is SIEM?', href: '/guides/what-is-siem' },
      { label: 'What is risk-based alerting?', href: '/guides/what-is-risk-based-alerting' },
      { label: 'Detection-as-code', href: '/guides/detection-as-code' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'ai-soc-analyst',
    title: 'What is an AI SOC analyst?',
    metaTitle: 'What Is an AI SOC Analyst? AI in Security Operations (2026) | LogPulse',
    description:
      'AI SOC analyst explained: software that triages alerts, investigates and writes verdicts at machine speed, what it does well, and why the human stays on the loop.',
    excerpt:
      'Software that triages alerts, investigates and writes verdicts at machine speed, what it does well, and where humans stay essential.',
    category: 'AI & Agents',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is an AI SOC analyst?',
        a: 'An AI SOC analyst (or AI SOC agent) is an AI system that performs front-line security-operations tasks: evaluating and enriching alerts, investigating, and producing a reasoned verdict with evidence. It is the analyst-shaped expression of the broader agentic SOC.',
      },
      {
        q: 'Does an AI SOC analyst replace human analysts?',
        a: 'No. The mature model is augmentation (human-on-the-loop): the AI handles alert volume and repetitive triage at machine speed, while humans own judgment, business context, novel threats, and consequential actions. Analysts shift from triaging alerts to supervising agent-led investigations.',
      },
      {
        q: 'What does an AI SOC analyst do?',
        a: 'It triages alerts (true vs false positive with context), investigates by gathering evidence and correlating signals, delivers a verdict with the reasoning, and recommends containment or response for a human to approve.',
      },
      {
        q: 'How do you trust an AI SOC analyst?',
        a: 'Through transparency: insist on citations, an evidence trail, and a human-approval gate before any action touches your environment. An AI analyst that shows its evidence can be verified and overridden; a black box cannot, which matters especially for regulated teams.',
      },
    ],
    related: [
      { label: 'What is an agentic SOC?', href: '/guides/what-is-an-agentic-soc' },
      { label: 'What is risk-based alerting?', href: '/guides/what-is-risk-based-alerting' },
      { label: 'Agentic SOC', href: '/agentic-soc' },
      { label: 'Security Monitoring (SIEM)', href: '/siem' },
    ],
  },
  {
    slug: 'ai-root-cause-analysis',
    title: 'AI root-cause analysis',
    metaTitle: 'AI Root-Cause Analysis for Logs & Incidents (2026) | LogPulse',
    description:
      'AI root-cause analysis explained: how an AI agent queries logs, correlates deploys and changes, and proposes a likely cause with evidence, so you verify in minutes, not hours.',
    excerpt:
      'How an AI agent queries logs, correlates deploys and changes, and proposes a likely cause with evidence. Verify in minutes, not hours.',
    category: 'AI & Agents',
    readTime: '6 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is AI root-cause analysis?',
        a: 'AI root-cause analysis (RCA) is AI-assisted investigation of why an incident happened. Given a symptom (an error spike, a latency jump, a failing service) an AI agent searches the relevant logs and telemetry, correlates them with deploys and configuration changes, and proposes the most likely cause with supporting evidence.',
      },
      {
        q: 'How does AI root-cause analysis work?',
        a: 'It starts from a symptom or natural-language question, generates and runs queries, correlates the timeline with recent deploys and config changes, pivots on what it finds to follow the trail, and proposes a likely root cause with linked evidence for a human to verify.',
      },
      {
        q: 'Does AI root-cause analysis replace engineers?',
        a: 'No. It does the tedious hunting so the engineer verifies a conclusion instead of starting from a blank query box. The human stays the owner of the conclusion; transparency (showing the queries and evidence) is what makes that verification possible.',
      },
      {
        q: 'How does LogPulse do AI root-cause analysis?',
        a: 'The LogPulse AI Investigator turns a natural-language question into LPQL, runs it on ClickHouse, correlates the timeline with deploys and changes, and proposes a likely root cause with linked evidence, showing every query so you stay in control. It is included on every plan.',
      },
    ],
    related: [
      { label: 'AI log search', href: '/ai-log-search' },
      { label: 'What is log management?', href: '/guides/what-is-log-management' },
      { label: 'AI SOC analyst', href: '/guides/ai-soc-analyst' },
    ],
  },
  {
    slug: 'what-is-a-log-collection-agent',
    title: 'What is a log collection agent?',
    metaTitle: 'What Is a Log Collection Agent? Log Shippers Explained (2026) | LogPulse',
    description:
      'Log collection agents (log shippers) explained: what they do, and how Vector, Fluent Bit, Fluentd, Filebeat and the OpenTelemetry Collector compare, plus how to choose.',
    excerpt:
      'What log shippers do, how Vector, Fluent Bit, Fluentd, Filebeat and the OpenTelemetry Collector compare, and how to choose one.',
    category: 'Log management',
    readTime: '7 min',
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    faq: [
      {
        q: 'What is a log collection agent?',
        a: 'A log collection agent (or log shipper) is a small program that runs on or near your systems, reads the logs they produce, optionally parses and transforms them, buffers them, and forwards them to a central log platform. It is the first stage of the log pipeline.',
      },
      {
        q: 'What are the main log shippers?',
        a: 'Common options are Vector (Rust, high-performance pipeline), Fluent Bit (C, extremely lightweight, default in many Kubernetes distros), Fluentd (Ruby, large plugin ecosystem), Filebeat (Go, lightweight file shipper from Elastic Beats), and the OpenTelemetry Collector (vendor-neutral, multi-signal for logs, metrics and traces).',
      },
      {
        q: 'Which log collection agent should I use?',
        a: 'It depends on resource footprint, reliability under your throughput, how much in-flight transformation you need, open-standards (OTLP) support, and operability at scale. Fluent Bit suits lightweight node-level collection, the OpenTelemetry Collector suits vendor-neutral unified telemetry, and Vector is a strong high-performance pipeline. Many estates combine them.',
      },
      {
        q: 'What agent does LogPulse use?',
        a: 'The LogPulse agent uses Vector as its runtime, wrapped in a control plane for fleet management (one-command install, server groups, per-agent authentication, EU-hosted control plane). LogPulse also accepts OTLP and a REST API, so you can ship with the OpenTelemetry Collector or any agent you already run.',
      },
    ],
    related: [
      { label: 'Agent Fleet', href: '/agents' },
      { label: 'What is log management?', href: '/guides/what-is-log-management' },
      { label: 'Structured logging', href: '/guides/structured-logging' },
    ],
  },
];

export function guideLocale(meta: GuideMeta): GuideLocale {
  return meta.locale ?? 'en';
}

/** Guides written in the given locale (the set a domain serves itself). */
export function guidesForLocale(locale: string): GuideMeta[] {
  return guides.filter((g) => guideLocale(g) === locale);
}

export function getGuide(slug: string, locale: string = 'en'): GuideMeta | undefined {
  return guides.find((g) => g.slug === slug && guideLocale(g) === locale);
}

/**
 * All language versions of a guide, for hreflang tags: the guide itself plus
 * its counterpart(s) via altOf, in both directions.
 */
export function guideAlternates(meta: GuideMeta): { locale: GuideLocale; slug: string }[] {
  // Resolve the English root, then collect it plus ALL its localized
  // siblings — every language version must list the complete set, in both
  // directions (Google requires reciprocal hreflang).
  const rootSlug = meta.altOf ?? meta.slug;
  const localized = guides
    .filter((g) => g.altOf === rootSlug)
    .map((g) => ({ locale: guideLocale(g), slug: g.slug }));
  return [{ locale: 'en' as GuideLocale, slug: rootSlug }, ...localized];
}

/** All guide route paths (hub + each guide) for the locale's build. */
export function guidePathsForLocale(locale: string): string[] {
  const own = guidesForLocale(locale);
  if (own.length === 0) return [];
  return ['/guides', ...own.map((g) => `/guides/${g.slug}`)];
}
